/* 儲存層。兩個不可妥協的設計：
   1. 實體與它的 op 在同一個交易裡落地 —— 不可能出現「改了內容但歷史沒記到」。
   2. 歷史是 append-only：回復舊版是「再寫一筆新 op 把舊值寫回去」，不是把時間倒轉。
   這一層刻意躲在介面後面：Web 用 IndexedDB，Capacitor 版換成 SQLite 外掛時只換實作。 */
import type {
  Category, Concept, ConceptStatus, ImportFingerprint, InboxItem, KnowledgeLink, OpReason, ReviewLog,
  Settings, SyncOp, ULID,
} from "../domain/types.ts";
import { applyOp, collectOps, diffFields, makeOp, pruneOps, restoreTo } from "../domain/oplog.ts";
import type { VersionEntry } from "../domain/oplog.ts";
import { buildSeedCategories, UNCATEGORIZED_ID } from "../domain/seed.ts";
import { newId } from "../domain/id.ts";
import { DAY_MS, TRASH_RETENTION_DAYS } from "../domain/rules.ts";
import { getAll, openDb, req, txDone } from "./idb.ts";

export interface ConceptPatch {
  /** 只在建立時使用：讓兩台裝置能算出同一個 id（衝突副本靠這個收斂）。 */
  id?: string;
  title?: string; summary?: string; body?: string; note?: string; source?: string;
  primaryCategoryId?: string; subCategoryIds?: string[];
  reviewDueAt?: number; reviewIntervalDays?: number;
  nsfw?: boolean; status?: ConceptStatus;
  /** 設為 null 表示「這不再是衝突副本」（兩條都保留時用）。 */
  conflictOf?: string | null;
  /** 來源檔案的指紋（匯入時記下，方便回溯它是從哪份文件來的）。 */
  contentHash?: string;
}

export interface SaveResult { concept: Concept; op: SyncOp | null }

export interface Repository {
  readonly deviceId: string;
  init(): Promise<void>;
  /** 只回傳沒被刪掉的抽屜；順序照 sortOrder（IndexedDB 的鍵序不是使用者定義的順序）。 */
  listCategories(): Promise<Category[]>;
  getCategory(id: ULID): Promise<Category | null>;
  saveCategory(patch: Partial<Category> & { name: string }, reason?: OpReason): Promise<Category>;
  /** 刪掉一個抽屜：裡面的知識退回「未分類」，副類別一起刪。回傳被搬動的知識數。 */
  deleteCategory(id: ULID): Promise<number>;
  upsertCategory(category: Category): Promise<void>;
  listConcepts(includeTrashed?: boolean): Promise<Concept[]>;
  getConcept(id: ULID): Promise<Concept | null>;
  saveConcept(existing: Concept | null, patch: ConceptPatch, reason?: OpReason): Promise<SaveResult>;
  setConceptStatus(id: ULID, status: ConceptStatus): Promise<SaveResult>;
  purgeExpiredTrash(force?: boolean): Promise<number>;
  listLinks(): Promise<KnowledgeLink[]>;
  saveLink(link: { fromId: string; toId: string; type: string; note?: string; id?: string }): Promise<KnowledgeLink>;
  deleteLink(id: ULID): Promise<void>;
  getLink(id: ULID): Promise<KnowledgeLink | null>;
  upsertLink(link: KnowledgeLink): Promise<void>;
  listOps(entityId?: ULID): Promise<SyncOp[]>;
  history(entityId: ULID): Promise<VersionEntry[]>;
  restoreConcept(id: ULID, targetRev: number): Promise<Concept>;
  logReview(entry: Omit<ReviewLog, "id">): Promise<ReviewLog>;
  listInbox(): Promise<InboxItem[]>;
  saveInbox(item: InboxItem): Promise<void>;
  deleteInbox(id: ULID): Promise<void>;
  putBlob(id: string, bytes: Uint8Array, mime: string): Promise<void>;
  getBlob(id: string): Promise<{ bytes: Uint8Array; mime: string } | null>;
  deleteBlob(id: string): Promise<void>;
  getSettings(): Promise<Settings>;
  saveSettings(patch: Partial<Settings>): Promise<Settings>;
  /* ── 同步用 ── */
  outbox(limit?: number): Promise<SyncOp[]>;
  clearOutbox(opIds: string[]): Promise<void>;
  getCursor(): Promise<number>;
  setCursor(seq: number): Promise<void>;
  getMeta<T>(key: string): Promise<T | null>;
  setMeta(key: string, value: unknown): Promise<void>;
  /** 遠端 op：直接寫進歷史，不進 outbox（它本來就是從遠端來的）。 */
  recordRemoteOp(op: SyncOp): Promise<void>;
  upsertConcept(concept: Concept): Promise<void>;
  /** 遠端帶來的 op 陣列（用來把本機沒有的實體從零重建）。 */
  opsForEntity(entityId: string): Promise<SyncOp[]>;
  /* ── 匯入 ── */
  getFingerprint(sha256: string): Promise<ImportFingerprint | null>;
  saveFingerprint(fp: ImportFingerprint): Promise<void>;
  close(): void;
}

const DEFAULTS = {
  reviewIntervalDays: 365,
  linkCapPerNode: 12,
};

export function defaultConcept(now: number, id: string): Concept {
  return {
    id, title: "", summary: "", body: "", note: "", source: "",
    primaryCategoryId: UNCATEGORIZED_ID, subCategoryIds: [],
    reviewDueAt: now + DEFAULTS.reviewIntervalDays * DAY_MS,
    reviewIntervalDays: DEFAULTS.reviewIntervalDays,
    nsfw: false, status: "draft", contentHash: "",
    createdAt: now, updatedAt: now, deletedAt: null, rev: 0,
  };
}

export class IdbRepository implements Repository {
  deviceId: string;
  private db: IDBDatabase;
  private clock: () => number;
  private seq: number;

  constructor(db: IDBDatabase, deviceId: string, clock: () => number, seq: number) {
    this.db = db;
    this.deviceId = deviceId;
    this.clock = clock;
    this.seq = seq;
  }

  /* ── 生命週期 ── */
  static async open(
    factory: IDBFactory, deviceId: string, clock: () => number = Date.now, dbName?: string,
  ): Promise<IdbRepository> {
    const db = await openDb(factory, dbName);
    const metaTx = db.transaction("meta", "readonly");
    const seqRow = await req<{ key: string; value: number } | undefined>(
      metaTx.objectStore("meta").get("seq") as IDBRequest<{ key: string; value: number } | undefined>);
    const repo = new IdbRepository(db, deviceId, clock, seqRow?.value ?? 0);
    await repo.init();
    return repo;
  }

  async init(): Promise<void> {
    /* 這裡刻意看「連刪掉的也算」的原始清單：否則你把 13 個種子類別全刪光之後，
       下次開啟又會把它們種回來 —— 刪除必須是永久的。 */
    const cats = await this.allCategories();
    if (cats.length === 0) {
      const now = this.clock();
      const seeds = buildSeedCategories(now);
      await this.writeMany("categories", seeds);
    }
    const s = await this.getSettings();
    if (!s.deviceId) await this.saveSettings({ deviceId: this.deviceId });
    await this.prune();
  }

  close(): void { this.db.close(); }

  /* ── 交易 ── */
  /** 實體、歷史、待推送佇列在同一個交易裡落地 ——
      不可能出現「內容改了但沒進同步佇列」這種永遠同步不到的狀態。 */
  private async writeEntityAndOp(store: string, entity: unknown, op: SyncOp | null): Promise<void> {
    const names = op ? [store, "ops", "outbox", "meta"] : [store];
    const tx = this.db.transaction(names, "readwrite");
    tx.objectStore(store).put(entity as Record<string, unknown>);
    if (op) {
      tx.objectStore("ops").put(op as unknown as Record<string, unknown>);
      tx.objectStore("outbox").put(op as unknown as Record<string, unknown>);
      this.seq += 1;
      tx.objectStore("meta").put({ key: "seq", value: this.seq });
    }
    await txDone(tx);
  }

  private async writeMany(store: string, values: unknown[]): Promise<void> {
    const tx = this.db.transaction(store, "readwrite");
    for (const v of values) tx.objectStore(store).put(v as Record<string, unknown>);
    await txDone(tx);
  }

  private nextSeq(): number { return this.seq + 1; }

  /* ── 類別 ── */
  /** 含已刪除的原始清單。只有 init 與刪除邏輯需要看它。 */
  private async allCategories(): Promise<Category[]> {
    const all = await getAll<Category>(this.db, "categories");
    return all.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }

  async listCategories(): Promise<Category[]> {
    return (await this.allCategories()).filter((c) => !c.deletedAt);
  }

  async getCategory(id: ULID): Promise<Category | null> {
    const tx = this.db.transaction("categories", "readonly");
    const row = await req<Category | undefined>(
      tx.objectStore("categories").get(id) as IDBRequest<Category | undefined>);
    return row ?? null;
  }

  async upsertCategory(category: Category): Promise<void> { await this.writeMany("categories", [category]); }

  /** 類別的改動也走 op 管線：改名、新增、刪除都會進待推送佇列，
      否則「在手機改的抽屜名字」到了電腦上會是舊的。 */
  async saveCategory(
    patch: Partial<Category> & { name: string }, reason: OpReason = "user-edit",
  ): Promise<Category> {
    const now = this.clock();
    const all = await this.allCategories();
    const existing = patch.id ? all.find((c) => c.id === patch.id) : undefined;

    if (existing) {
      const changed = diffFields(existing, patch);
      if (Object.keys(changed).length === 0) return existing;
      changed["updatedAt"] = { from: existing.updatedAt, to: now };
      const op = makeOp({
        entityType: "category", entityId: existing.id, baseRev: existing.rev, changedFields: changed,
        reason, deviceId: this.deviceId, seq: this.nextSeq(), at: now, idFor: () => newId(now),
      });
      const next = applyOp(existing, op);
      await this.writeEntityAndOp("categories", next, op);
      return next;
    }

    const draft: Category = {
      id: patch.id ?? newId(now), parentId: patch.parentId ?? null, name: patch.name,
      definition: patch.definition ?? "", reviewDefaultDays: patch.reviewDefaultDays ?? DEFAULTS.reviewIntervalDays,
      linkCapPerNode: patch.linkCapPerNode ?? DEFAULTS.linkCapPerNode,
      sortOrder: patch.sortOrder ?? all.filter((c) => !c.deletedAt).length, system: false,
      createdAt: now, updatedAt: now, deletedAt: null, rev: 0,
    };
    const op = makeOp({
      entityType: "category", entityId: draft.id, baseRev: 0,
      changedFields: diffFields({}, draft as unknown as object),
      reason, deviceId: this.deviceId, seq: this.nextSeq(), at: now, idFor: () => newId(now),
    });
    const created = applyOp(draft, op);
    await this.writeEntityAndOp("categories", created, op);
    return created;
  }

  /** 刪抽屜不能連裡面的東西一起消失。退回「未分類」是一筆正常的 op，
      所以另一台裝置會看到同一件事發生 —— 而不是兩邊各說各話。 */
  async deleteCategory(id: ULID): Promise<number> {
    const target = await this.getCategory(id);
    if (!target) return 0;
    if (target.system) throw new Error("系統保留的類別不能刪除。");

    const all = await this.allCategories();
    const children = all.filter((c) => c.parentId === id && !c.deletedAt);
    const goingAway = new Set([id, ...children.map((c) => c.id)]);

    let moved = 0;
    for (const concept of await this.listConcepts(true)) {
      const patch: ConceptPatch = {};
      if (concept.primaryCategoryId === id) patch.primaryCategoryId = UNCATEGORIZED_ID;
      const kept = concept.subCategoryIds.filter((s) => !goingAway.has(s));
      if (kept.length !== concept.subCategoryIds.length) patch.subCategoryIds = kept;
      if (Object.keys(patch).length) {
        await this.saveConcept(concept, patch, "soft-delete");
        moved += 1;
      }
    }
    const now = this.clock();
    for (const c of [...children, target]) {
      await this.saveCategory({ id: c.id, name: c.name, deletedAt: now }, "soft-delete");
    }
    return moved;
  }

  /* ── 知識 ── */
  async listConcepts(includeTrashed = false): Promise<Concept[]> {
    const all = await getAll<Concept>(this.db, "concepts");
    return includeTrashed ? all : all.filter((c) => c.status !== "trashed");
  }

  async getConcept(id: ULID): Promise<Concept | null> {
    const tx = this.db.transaction("concepts", "readonly");
    const row = await req<Concept | undefined>(tx.objectStore("concepts").get(id) as IDBRequest<Concept | undefined>);
    return row ?? null;
  }

  async saveConcept(existing: Concept | null, patch: ConceptPatch, reason: OpReason = "user-edit"): Promise<SaveResult> {
    const now = this.clock();
    if (!existing) {
      const draft = { ...defaultConcept(now, patch.id ?? newId(now)), ...patch };
      const changed = diffFields({}, draft as unknown as object);
      const op = makeOp({
        entityType: "concept", entityId: draft.id, baseRev: 0, changedFields: changed,
        reason, deviceId: this.deviceId, seq: this.nextSeq(), at: now, idFor: () => newId(now),
      });
      const created = applyOp({ ...draft, rev: 0 }, op);
      await this.writeEntityAndOp("concepts", created, op);
      return { concept: created, op };
    }

    const changed = diffFields(existing, patch);
    if (Object.keys(changed).length === 0) return { concept: existing, op: null };
    changed["updatedAt"] = { from: existing.updatedAt, to: now };
    const op = makeOp({
      entityType: "concept", entityId: existing.id, baseRev: existing.rev, changedFields: changed,
      reason, deviceId: this.deviceId, seq: this.nextSeq(), at: now, idFor: () => newId(now),
    });
    const next = applyOp(existing, op);
    await this.writeEntityAndOp("concepts", next, op);
    return { concept: next, op };
  }

  async setConceptStatus(id: ULID, status: ConceptStatus): Promise<SaveResult> {
    const existing = await this.getConcept(id);
    if (!existing) throw new Error("找不到這條知識：" + id);
    const patch: ConceptPatch = { status };
    if (status === "trashed") {
      const now = this.clock();
      const changed = diffFields(existing, patch);
      changed["deletedAt"] = { from: existing.deletedAt, to: now };
      changed["updatedAt"] = { from: existing.updatedAt, to: now };
      const op = makeOp({ entityType: "concept", entityId: id, baseRev: existing.rev, changedFields: changed,
        reason: "soft-delete", deviceId: this.deviceId, seq: this.nextSeq(), at: now, idFor: () => newId(now) });
      const next = applyOp(existing, op);
      await this.writeEntityAndOp("concepts", next, op);
      return { concept: next, op };
    }
    if (existing.deletedAt !== null) {
      const now = this.clock();
      const changed = diffFields(existing, patch);
      changed["deletedAt"] = { from: existing.deletedAt, to: null };
      changed["updatedAt"] = { from: existing.updatedAt, to: now };
      const op = makeOp({ entityType: "concept", entityId: id, baseRev: existing.rev, changedFields: changed,
        reason: "user-edit", deviceId: this.deviceId, seq: this.nextSeq(), at: now, idFor: () => newId(now) });
      const next = applyOp(existing, op);
      await this.writeEntityAndOp("concepts", next, op);
      return { concept: next, op };
    }
    return this.saveConcept(existing, patch);
  }

  /** 垃圾桶滿 30 天就真的刪掉：連同它的棉線與歷史。force=true 時立刻清空。 */
  async purgeExpiredTrash(force = false): Promise<number> {
    const now = this.clock();
    const all = await getAll<Concept>(this.db, "concepts");
    const dead = all.filter((c) => c.deletedAt !== null
      && (force || now - c.deletedAt > TRASH_RETENTION_DAYS * DAY_MS));
    if (!dead.length) return 0;
    const links = await this.listLinks();
    const ops = await this.listOps();
    const deadIds = new Set(dead.map((c) => c.id));
    const tx = this.db.transaction(["concepts", "links", "ops"], "readwrite");
    const cs = tx.objectStore("concepts");
    for (const c of dead) cs.delete(c.id);
    const ls = tx.objectStore("links");
    for (const l of links) if (deadIds.has(l.fromId) || deadIds.has(l.toId)) ls.delete(l.id);
    const os = tx.objectStore("ops");
    for (const o of ops) if (deadIds.has(o.entityId)) os.delete(o.opId);
    await txDone(tx);
    return dead.length;
  }

  /* ── 棉線 ── */
  async listLinks(): Promise<KnowledgeLink[]> {
    const all = await getAll<KnowledgeLink>(this.db, "links");
    return all.filter((l) => l.deletedAt === null);
  }

  /** 連結也要走 op 管線 —— 否則「棉線」永遠不會同步到另一台裝置。 */
  async saveLink(input: { fromId: string; toId: string; type: string; note?: string; id?: string }): Promise<KnowledgeLink> {
    const now = this.clock();
    const existing = input.id ? await this.getLink(input.id) : null;
    const draft: KnowledgeLink = {
      id: input.id ?? newId(now), fromId: input.fromId, toId: input.toId, type: input.type,
      note: input.note ?? "", createdAt: existing?.createdAt ?? now, updatedAt: now,
      deletedAt: existing?.deletedAt ?? null, rev: existing?.rev ?? 0,
    };
    const changed = diffFields(existing ?? {}, draft);
    if (existing && !Object.keys(changed).length) return existing;
    const op = makeOp({
      entityType: "link", entityId: draft.id, baseRev: existing?.rev ?? 0, changedFields: changed,
      reason: "user-edit", deviceId: this.deviceId, seq: this.nextSeq(), at: now, idFor: () => newId(now),
    });
    const next = applyOp(draft, op);
    await this.writeEntityAndOp("links", next, op);
    return next;
  }

  /** 軟刪除：寫一筆 deletedAt 的 op，另一台裝置才收得到「這條棉線被剪了」。 */
  async deleteLink(id: ULID): Promise<void> {
    const existing = await this.getLink(id);
    if (!existing) return;
    const now = this.clock();
    const changed = diffFields(existing, { deletedAt: now, updatedAt: now });
    const op = makeOp({
      entityType: "link", entityId: id, baseRev: existing.rev, changedFields: changed,
      reason: "soft-delete", deviceId: this.deviceId, seq: this.nextSeq(), at: now, idFor: () => newId(now),
    });
    await this.writeEntityAndOp("links", applyOp(existing, op), op);
  }

  async getLink(id: ULID): Promise<KnowledgeLink | null> {
    const tx = this.db.transaction("links", "readonly");
    const row = await req<KnowledgeLink | undefined>(tx.objectStore("links").get(id) as IDBRequest<KnowledgeLink | undefined>);
    return row ?? null;
  }

  async upsertLink(link: KnowledgeLink): Promise<void> { await this.writeMany("links", [link]); }

  /* ── 歷史 ── */
  async listOps(entityId?: ULID): Promise<SyncOp[]> {
    const all = await getAll<SyncOp>(this.db, "ops");
    return entityId ? all.filter((o) => o.entityId === entityId) : all;
  }

  async history(entityId: ULID): Promise<VersionEntry[]> {
    const current = await this.getConcept(entityId);
    if (!current) return [];
    const ops = await this.listOps(entityId);
    const entries = collectOps(ops, entityId).map((o) => ({
      rev: o.baseRev + 1, at: o.at, reason: o.reason, fields: Object.keys(o.changedFields),
    }));
    return [...entries, { rev: current.rev, at: current.updatedAt, reason: "user-edit" as OpReason, fields: [] }]
      .sort((a, b) => b.rev - a.rev);
  }

  /** 回復舊版＝再寫一筆 op 把舊值寫回去。歷史永遠不會被改寫。 */
  async restoreConcept(id: ULID, targetRev: number): Promise<Concept> {
    const current = await this.getConcept(id);
    if (!current) throw new Error("找不到這條知識：" + id);
    if (targetRev >= current.rev) return current;
    const ops = await this.listOps(id);
    const restored = restoreTo(current, ops, targetRev);
    const patch: ConceptPatch = {};
    for (const key of ["title", "summary", "body", "note", "source", "primaryCategoryId", "reviewDueAt",
      "reviewIntervalDays", "nsfw", "status", "deletedAt"] as (keyof ConceptPatch)[]) {
      (patch as Record<string, unknown>)[key] = (restored as unknown as Record<string, unknown>)[key];
    }
    const res = await this.saveConcept(current, patch, "restore");
    return res.concept;
  }

  /** 保留策略：每條知識最近 20 版或 30 天內（先到者為準）。 */
  async prune(): Promise<number> {
    const ops = await this.listOps();
    const kept = pruneOps(ops, this.clock());
    if (kept.length === ops.length) return 0;
    const keepIds = new Set(kept.map((o) => o.opId));
    const tx = this.db.transaction("ops", "readwrite");
    const store = tx.objectStore("ops");
    for (const o of ops) if (!keepIds.has(o.opId)) store.delete(o.opId);
    await txDone(tx);
    return ops.length - kept.length;
  }

  /* ── 複習紀錄 ── */
  async logReview(entry: Omit<ReviewLog, "id">): Promise<ReviewLog> {
    const now = this.clock();
    const full: ReviewLog = { ...entry, id: newId(now) };
    await this.writeMany("reviewLogs", [full]);
    return full;
  }

  /* ── 托盤 ── */
  listInbox(): Promise<InboxItem[]> { return getAll<InboxItem>(this.db, "inbox"); }
  async saveInbox(item: InboxItem): Promise<void> { await this.writeMany("inbox", [item]); }

  /* ── 同步 ── */
  async outbox(limit = 200): Promise<SyncOp[]> {
    const all = await getAll<SyncOp>(this.db, "outbox");
    return all.sort((a, b) => a.at - b.at || a.seq - b.seq).slice(0, limit);
  }

  async clearOutbox(opIds: string[]): Promise<void> {
    if (!opIds.length) return;
    const tx = this.db.transaction("outbox", "readwrite");
    const store = tx.objectStore("outbox");
    for (const id of opIds) store.delete(id);
    await txDone(tx);
  }

  async getCursor(): Promise<number> {
    const row = await this.getMeta<number>("cursor");
    return row ?? 0;
  }

  setCursor(seq: number): Promise<void> { return this.setMeta("cursor", seq); }

  async getMeta<T>(key: string): Promise<T | null> {
    const tx = this.db.transaction("meta", "readonly");
    const row = await req<{ key: string; value: T } | undefined>(
      tx.objectStore("meta").get(key) as IDBRequest<{ key: string; value: T } | undefined>);
    return row ? row.value : null;
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    await this.writeMany("meta", [{ key, value }]);
  }

  /** 遠端 op：寫進歷史供版本回復與衝突判定，但不再排進待推送佇列。 */
  async recordRemoteOp(op: SyncOp): Promise<void> {
    const tx = this.db.transaction("ops", "readwrite");
    tx.objectStore("ops").put(op as unknown as Record<string, unknown>);
    await txDone(tx);
  }

  async upsertConcept(concept: Concept): Promise<void> {
    await this.writeMany("concepts", [concept]);
  }

  async opsForEntity(entityId: string): Promise<SyncOp[]> {
    const all = await this.listOps(entityId);
    return all.sort((a, b) => a.seq - b.seq || a.at - b.at);
  }

  /* ── 托盤的素材本體 ──
     只在「未編目」期間存在；壓製成知識或刪除之後就清掉（原檔不落地）。 */
  async putBlob(id: string, bytes: Uint8Array, mime: string): Promise<void> {
    await this.writeMany("blobs", [{ id, mime, bytes, bytes_len: bytes.byteLength, at: this.clock() }]);
  }

  async getBlob(id: string): Promise<{ bytes: Uint8Array; mime: string } | null> {
    const tx = this.db.transaction("blobs", "readonly");
    const row = await req<{ id: string; mime: string; bytes: Uint8Array } | undefined>(
      tx.objectStore("blobs").get(id) as IDBRequest<{ id: string; mime: string; bytes: Uint8Array } | undefined>);
    return row ? { bytes: row.bytes, mime: row.mime } : null;
  }

  async deleteBlob(id: string): Promise<void> {
    const tx = this.db.transaction("blobs", "readwrite");
    tx.objectStore("blobs").delete(id);
    await txDone(tx);
  }

  async deleteInbox(id: ULID): Promise<void> {
    const tx = this.db.transaction("inbox", "readwrite");
    tx.objectStore("inbox").delete(id);
    await txDone(tx);
  }

  /* ── 匯入指紋：永久保留，用來擋下重複導入同一份文件 ── */
  async getFingerprint(sha256: string): Promise<ImportFingerprint | null> {
    const tx = this.db.transaction("fingerprints", "readonly");
    const row = await req<ImportFingerprint | undefined>(
      tx.objectStore("fingerprints").get(sha256) as IDBRequest<ImportFingerprint | undefined>);
    return row ?? null;
  }

  async saveFingerprint(fp: ImportFingerprint): Promise<void> {
    await this.writeMany("fingerprints", [fp]);
  }

  /* ── 設定 ── */
  async getSettings(): Promise<Settings> {
    const tx = this.db.transaction("meta", "readonly");
    const row = await req<{ key: string; value: Settings } | undefined>(
      tx.objectStore("meta").get("settings") as IDBRequest<{ key: string; value: Settings } | undefined>);
    return row?.value ?? {
      deviceId: "", deviceName: "這台裝置", locale: "auto", sheetMaterial: "night", typeScale: 1,
      adultUnlocked: false, reviewDefaultDays: 365,
    };
  }

  async saveSettings(patch: Partial<Settings>): Promise<Settings> {
    const next = { ...(await this.getSettings()), ...patch };
    await this.writeMany("meta", [{ key: "settings", value: next }]);
    return next;
  }
}

export function openRepository(
  factory: IDBFactory, deviceId: string, clock: () => number = Date.now, dbName?: string,
): Promise<IdbRepository> {
  return IdbRepository.open(factory, deviceId, clock, dbName);
}
